import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getGlobalConfig, GlobalConfig } from '@adornis/bh-matching-config/entities/global-config.js';
import { BuddyPageSignup } from '@adornis/bh-matching-signup/components/buddy-page-signup.js';
import '@adornis/bh-matching-signup/components/matching-comp-signup-closed.js';
import { currentlySelectedTimeframe } from '@adornis/bh-matching-timeframes/components/matching-semester-selection.js';
import { MatchingUserAPI } from '@adornis/bh-matching-user/api/MatchingUserAPI.js';
import { Gender } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingPermission } from '@adornis/bh-matching-user/permissions.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
import '@adornis/buchungsheld-theme/components/tr-section.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { XImage } from '@adornis/file-utils/client/x-image.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { Language } from '@adornis/translation-core/languages.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { type Ref, createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import {
  FachbereichEnum,
  MeetingType,
  MentorFilter,
  MentoringMentee,
  MentoringMentor,
} from '../entities/mentoring-user.js';
import type { TranslationDictionaryMentoring } from '../translations.js';
import './components/mentoring-ffm-inline-selection.js';
import { mentorVisibilityToggle } from './components/misc.js';
import { menteeCustomFieldMapping } from './form-mapping.js';

@customElement('mentoring-mentee-signup')
export class MentoringMenteeSignup extends ChemistryLitElement {
  @state() protected readonly _translation = new TranslationController<TranslationDictionaryMentoring>(this);
  @state() private readonly _language = new RXController(this, globalLanguageSubject);
  @state() protected readonly _size = new WindowSizeController(this);

  @state() private _currentlySelectedTimeframe = new RXController(this, currentlySelectedTimeframe);

  @state() private _selectedMentorIDs = new RXController<string[]>(this, []);
  @state() protected readonly _config = new RXController(this, getGlobalConfig()(selectionSet(() => GlobalConfig, 2)));
  @state() protected readonly _currUser = new RXController(this, currentUser());

  // TODO use virtualized list
  private _mentorFilter = new MentorFilter({});
  @state() private _mentors = new RXController(
    this,
    combineLatest([
      this._currentlySelectedTimeframe.observable,
      this._mentorFilter.whenChanging,
      this._currUser.observable,
    ]).pipe(
      switchMap(([timeframe, filter, user]) => {
        let mentors = timeframe
          ? (MatchingUserAPI.subscribeAllInCurrentTimeframe(
              MentoringMentor._class,
              timeframe._id,
              filter,
            )(selectionSet(() => MentoringMentor, 2)) as Observable<MentoringMentor[]>)
          : of([]);
        const isAdmin = user?.hasPermission(MatchingPermission.VIEW_ADMIN_DASHBOARD);
        if (!isAdmin) {
          mentors = mentors.pipe(map(mentors => mentors.filter(m => !m.hiddenFromMentees)));
        }
        return mentors;
      }),
    ),
  );

  @state() private showingList = true;

  override render() {
    if (!this._currentlySelectedTimeframe.value)
      return html`
        <tr-section>
          <matching-comp-signup-closed></matching-comp-signup-closed>
        </tr-section>
      `;

    const currentTimeframe = this._currentlySelectedTimeframe.value;
    if (this.showingList) {
      return html`
        <tr-section padding="md" space="lg">
          <tr-flex horizontal crossaxis-end space-between space="lg">
            <tr-flex space="md">
              <tr-h1> ${this._translation.translate('mentoring', 'menteeSignupTitle')} </tr-h1>
              ${currentTimeframe
                ? html` <tr-text tone="subtle"> Semester: ${currentTimeframe.name} </tr-text> `
                : nothing}
            </tr-flex>
            <tr-button-small
              mode="outline"
              .action=${() => {
                if (this._language.value === Language.GERMAN) {
                  void clientTranslator.selectLanguage(Language.ENGLISH);
                } else {
                  void clientTranslator.selectLanguage(Language.GERMAN);
                }

                // TODO this is need because else not every translation changes (i.e. sidebar items)
                window.location.reload();
              }}
            >
              <tr-text>${this._language.value}</tr-text>
              <tr-tooltip .text=${this._translation.translate('change_language')}></tr-tooltip>
            </tr-button-small>
          </tr-flex>
          <tr-flex horizontal end flex>
            <tr-button
              @click=${() => {
                this.showingList = !this.showingList;
              }}
            >
              ${this._translation.translate('NEW_MENTEE_SIGNUP')}
              ${this._selectedMentorIDs.value.length > 0 ? `(${this._selectedMentorIDs.value.length})` : ''}
            </tr-button>
          </tr-flex>
          <tr-flex space="md" ?horizontal=${!this._size.mobile}>
            <tr-dropdown-selection
              flex
              clearable
              placeholder=${this._translation.translate('field', 'department')}
              .selectables=${Object.keys(FachbereichEnum)
                .filter(key => !key.startsWith('_'))
                .map(key => FachbereichEnum[key])}
              .renderString=${(value: FachbereichEnum) => this._translation.translate('enums', value)}
              @value-picked=${(e: ValueEvent<FachbereichEnum>) => {
                this._mentorFilter.department = e.detail.value;
              }}
            ></tr-dropdown-selection>
            <tr-dropdown-selection
              flex
              clearable
              placeholder=${this._translation.translate('field', 'gender')}
              .selectables=${Object.keys(Gender)
                .filter(key => !key.startsWith('_'))
                .map(key => Gender[key])}
              .renderString=${(value: Gender) => this._translation.translate('enums', value)}
              @value-picked=${(e: ValueEvent<Gender>) => {
                this._mentorFilter.gender = e.detail.value;
              }}
            ></tr-dropdown-selection>
            <tr-dropdown-selection
              flex
              clearable
              placeholder=${this._translation.translate('field', 'meetingType')}
              .selectables=${Object.keys(MeetingType)
                .filter(key => !key.startsWith('_'))
                .map(key => MeetingType[key])}
              .renderString=${(value: MeetingType) => this._translation.translate('enums', value)}
              @value-picked=${(e: ValueEvent<MeetingType>) => {
                this._mentorFilter.meetingType = e.detail.value;
              }}
            ></tr-dropdown-selection>
          </tr-flex>
          ${this._mentors.isLoading
            ? html`<tr-flex h100 flex center crossaxis-center> <tr-loader></tr-loader> </tr-flex>`
            : html`<tr-flex h100 flex center crossaxis-center> ${this._renderMentors()} </tr-flex>`}
        </tr-section>
      `;
    } else {
      return html`
        <mentoring-ffm-signup-form
          .userClass=${MentoringMentee}
          page-title=${this._translation.translate('mentoring', 'menteeSignupTitle')}
          .customFieldMapping=${menteeCustomFieldMapping}
          .backCallback=${() => {
            this.showingList = !this.showingList;
          }}
          .prefereredMentors=${this._selectedMentorIDs.value}
        ></mentoring-ffm-signup-form>
      `;
    }
  }

  private _renderMentors() {
    if (this._mentors.value && this._mentors.value.length > 0) {
      return html`
        <tr-flex w100 space="md"> ${repeat(this._mentors.value!, mentor => this._mentorCard(mentor))} </tr-flex>
      `;
    }
    return html` <tr-empty-placeholder
      .img=${'close'}
      .text=${'Noch keine Mentoren verfügbar'}
      no-wrapper
    ></tr-empty-placeholder>`;
  }

  private _mentorCard(mentor: MentoringMentor) {
    const fieldToShow = [
      'gender',
      'age',
      'mentorSprache',
      'studium',
      'abschluss',
      'currentPosition',
      'currentOcupationFocus',
      'berufserfahrung',
      'sonstigesErfahrungen',
      'motivationMentor',
      'menteeDepartments',
      'meetingType',
    ];
    const allSelections = this._selectedMentorIDs.value ?? [];
    const isSelected = allSelections.includes(mentor._id);

    return html`
      <tr-basic-card
        space="md"
        padding="xxs"
        w100
        @click=${() => {
          let ids = this._selectedMentorIDs.value;
          if (!isSelected) {
            if (allSelections.length >= 3) return XSnackbar.show('Es können maximal 3 Mentoren ausgewählt werden.');
            ids.push(mentor._id);
          } else {
            ids = ids.filter(id => id !== mentor._id);
          }
          this._selectedMentorIDs.next(ids);
        }}
        ${css(
          isSelected
            ? { border: `1px solid ${this.colors.accent}`, background: this.colors.accent.shade(50).opacity(0.333) }
            : {},
        )}
      >
        <tr-flex space="md" horizontal end>
          ${mentorVisibilityToggle({ mentor, translate: this._translation.translate })}
          <tr-checkbox .value=${isSelected}></tr-checkbox>
        </tr-flex>

        <tr-flex space="md" padding="sm">
          ${repeat(fieldToShow, field => {
            return this._mentorCardField(field, mentor[field]);
          })}
        </tr-flex>
      </tr-basic-card>
    `;
  }

  private _mentorCardField(name: string, value: any) {
    const translation = Array.isArray(value)
      ? value
          .map(v => {
            const translation = this._translation.translate('enums', v);
            return translation.startsWith('enums') ? v : translation;
          })
          .join(', ')
      : this._translation.translate('enums', value);
    const finalTranslation = translation.startsWith('enums') ? value : translation;

    return html`
      <tr-flex horizontal space="md" padding="xss">
        <tr-flex ${css({ minWidth: '33%', maxWidth: '33%' })}
          ><tr-text bold> ${this._translation.translate('field', name)}: </tr-text></tr-flex
        >
        <tr-text> ${finalTranslation || '-'} </tr-text>
      </tr-flex>
    `;
  }
}

/*
 * @element mentoring-ffm-signup
 */
@customElement('mentoring-ffm-signup-form')
export class MentoringFfmSignupForm extends BuddyPageSignup {
  @property({ attribute: false }) backCallback?: () => void;

  @property({ attribute: false }) prefereredMentors?: string[];

  imageRef: Ref<XImage> = createRef();

  protected override async _registerUser(user: MentoringMentee) {
    if ((this.prefereredMentors && !user.preferedMentorsIDs) || user.preferedMentorsIDs?.length === 0)
      user.preferedMentorsIDs = this.prefereredMentors;
    await super._registerUser(user);
  }

  protected override _header() {
    return html`
      <tr-flex horizontal crossaxis-end space-between space="lg">
        <tr-flex space="md">
          <tr-h1> ${this.pageTitle} </tr-h1>
          ${this._currentlySelectedSemester.value
            ? html` <tr-text tone="subtle"> Semester: ${this._currentlySelectedSemester.value.name} </tr-text> `
            : nothing}
        </tr-flex>
        <tr-button-small
          mode="outline"
          .action=${() => {
            if (this._language.value === Language.GERMAN) {
              void clientTranslator.selectLanguage(Language.ENGLISH);
            } else {
              void clientTranslator.selectLanguage(Language.GERMAN);
            }

            // TODO this is need because else not every translation changes (i.e. sidebar items)
            window.location.reload();
          }}
        >
          <tr-text>${this._language.value}</tr-text>
          <tr-tooltip .text=${this._translation.translate('change_language')}></tr-tooltip>
        </tr-button-small>
      </tr-flex>
      ${this.prefereredMentors?.length
        ? html`
            <tr-text tone="subtle">
              ${this._translation.translate('signup_mentee', 'preferredMentorsSelected')}:
              ${this.prefereredMentors.length}
            </tr-text>
          `
        : nothing}
      <tr-button mode="icon" @click=${this.backCallback} icon="arrow_back"> </tr-button>
    `;
  }

  protected override _personalDataContent() {
    const fields = Object.values(this.userClass.value?.fields ?? {});

    const personalDataFields = [
      'minSemesterPrerequisite',
      'languageC1',
      'firstName',
      'lastName',
      'gender',
      'age',
      'staatsangehoerigkeit',
      'matrikelNummer',
      'email',
      'emailRepeat',
      'phone',
      'privacyPolicy',
    ];

    const additionalDataFields = [
      'preferredLanguage',
      'whatIsNeededFromMentor',
      'department',
      'mainSubject',
      'extraSubject',
      'menteeSemester',
      'angestrebterAbschluss',
      'genderPreference',
      'meetingType',
      'sonstigesNotes',
      'motivationMentee',
      'firstTime',
      'professionField',
      'naechsteKarriereschritte',
      'searchingWork',
      'expectedGraduationYear',
      'expectedGraduationMonat',
      'firstGenerationStudent',
      'immigrantHistory',
      'studiedAbroad',
      'erasmusStudent',
      'businessProfile',
      'discoveredFrom',
    ];

    return html`
      <tr-card ${css({ width: '100%' })}>
        <tr-h3> ${this._translation.translate('signup_mentee', 'profile_image')} </tr-h3>
        <tr-flex horizontal crossaxis-center space="md">
          <tr-icon ${css({ color: this.colors.neutral.shade(100) })}> info </tr-icon>
          <tr-text> ${this._translation.translate('signup_mentee', 'profile_image_info')} </tr-text>
        </tr-flex>
        <tr-flex center crossaxis-center>
          <tr-image
            ${this._form.field('profilePicture')}
            mode="cover"
            ${css({ width: '200px', height: '200px', borderRadius: '50%', overflow: 'hidden' })}
            clearable
            ${ref(this.imageRef)}
          ></tr-image>
          <tr-button
            mode="icon"
            icon="close"
            @click=${() => {
              if (!this._form.document) return;
              if (!this.imageRef.value) return;
              this._form.document.profilePicture = undefined;
              this.imageRef.value.clear();
              this.requestUpdate();
            }}
          ></tr-button>
        </tr-flex>
      </tr-card>
      <tr-card ${css({ width: '100%' })}>
        <tr-h3> ${this._translation.translate('signup_mentee', 'personal_data')} </tr-h3>
        <tr-grid columns="1fr 2fr" space="md">
          ${this._fields(
            fields
              .filter(field => personalDataFields.includes(field.name))
              .sort((a, b) => personalDataFields.indexOf(a.name) - personalDataFields.indexOf(b.name)),
          )}
        </tr-grid>
      </tr-card>
      <tr-card ${css({ width: '100%' })}>
        <tr-h3> ${this._translation.translate('signup_mentee', 'additional_data')} </tr-h3>
        <tr-grid columns="1fr 2fr" space="md">
          ${this._fields(
            fields
              .filter(field => additionalDataFields.includes(field.name))
              .sort((a, b) => additionalDataFields.indexOf(a.name) - additionalDataFields.indexOf(b.name)),
          )}
        </tr-grid>
      </tr-card>
      <tr-card ${css({ width: '100%' })}>
        <tr-h3> ${this._translation.translate('signup_mentee', 'cv')} </tr-h3>
        <tr-text> ${this._translation.translate('signup_mentee', 'cv_info')} </tr-text>
        <tr-file-input
          clearable
          accept="application/pdf"
          ${this._form.field('curriculumVitae')}
          placeholder=${this._translation.translate('field', 'curriculumVitae')}
        ></tr-file-input>
      </tr-card>

      <tr-text bold>${this._translation.translate('signup_mentee', 'required_fields')}</tr-text>
      <tr-text>${this._translation.translate('signup_mentee', 'general_info')}</tr-text>
      <tr-text
        >${this._translation.translate('signup_mentee', 'questions_info')}
        <a href="mailto:mentoring@uni-frankfurt.de">mentoring@uni-frankfurt.de</a></tr-text
      >
    `;
  }

  protected override _fields(fields: BaseQLFieldDefinition[]) {
    return html`
      ${fields
        .filter(field => !userFieldBlackList.includes(field.name))
        .map(field => {
          const infoTranslation = this._translation.translate('field_description', field.name);
          return (
            this.customFieldMapping?.(field, this._form, this._translation, this) ??
            html`
              <tr-flex ${css({ gridColumn: '1 / -1' })}>
                ${this._form.genericField([field.name], {
                  placeholderMode: 'static-floating',
                  info: infoTranslation !== `field_description.${field.name}` ? infoTranslation : undefined,
                })}
              </tr-flex>
            `
          );
        })}
    `;
  }
}
